import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";
import { useToast } from "../../hooks/useToast";
import DateUtil from "../../util/DateUtil";
import { formatHistoryVersion } from "../../util/formatHistoryVersion";

const PASS_CONFIGURATION_DOCUMENT_TYPE = "PassConfiguration";

const INFO_TEXT =
  "Please note that restoring a version will revert all configuration settings. Uploaded images (logos, strips, headers) will remain as the latest versions.";

interface PassConfigurationUpdateHistoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  passConfiguration: CherryHubAdminApi.CherryPassConfigurationSummaryItem | null;
}

const formatLastUpdated = (dateUpdated: string | null | undefined): string => {
  if (!dateUpdated) {
    return "—";
  }

  const date = new Date(dateUpdated);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString("en-AU", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
};

export const PassConfigurationUpdateHistoryDrawer = ({
  isOpen,
  onClose,
  businessId,
  passConfiguration,
}: PassConfigurationUpdateHistoryDrawerProps) => {
  const apiClient = useAdminApiClient();
  const navigate = useNavigate();
  const { showErrorToast, showSuccessToast } = useToast();
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();
  const [historyItems, setHistoryItems] = useState<
    CherryHubAdminApi.DocumentHistoryItem[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [revertingHistoryId, setRevertingHistoryId] = useState<string | null>(
    null
  );

  const documentId = passConfiguration?.Meta?.Id;
  const passName = passConfiguration?.DisplayName ?? "";

  useEffect(() => {
    if (!isOpen || !documentId) {
      setHistoryItems([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const loadHistory = async () => {
      setIsLoading(true);
      try {
        const result = await apiClient.getDocumentHistory(
          businessId,
          PASS_CONFIGURATION_DOCUMENT_TYPE,
          documentId
        );

        if (cancelled) {
          return;
        }

        if (!result.ok) {
          showErrorToast(
            result.message || "Failed to load pass configuration history."
          );
          setHistoryItems([]);
          return;
        }

        setHistoryItems(result.data.Results ?? []);
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error("Load pass configuration history failed:", error);
        showErrorToast(
          "An error occurred while loading pass configuration history."
        );
        setHistoryItems([]);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [apiClient, businessId, documentId, isOpen, showErrorToast]);

  const handleRevert = async (item: CherryHubAdminApi.DocumentHistoryItem) => {
    if (!documentId || !item.HistoryId || revertingHistoryId) {
      return;
    }

    const versionLabel =
      item.Version == null ? "Unversioned" : String(item.Version);
    const lastModified = DateUtil.getLocalDateOnlyPaddedFormatFromString(
      item.Meta?.DateUpdated
    );
    const { ok: confirmed } = await showConfirmationDialog(
      "Revert to previous version?",
      `Restoring a version will revert all configuration settings. Uploaded images (logos, strips, headers) will remain as the latest versions. Are you sure you want to revert to version ${versionLabel} (${lastModified})?`,
      "Confirm Revert"
    );

    if (!confirmed) {
      return;
    }

    setRevertingHistoryId(item.HistoryId);
    try {
      const result = await apiClient.revertPassConfiguration(
        businessId,
        documentId,
        item.HistoryId
      );

      if (!result.ok) {
        showErrorToast(
          result.message || "Failed to revert pass configuration."
        );
        return;
      }

      showSuccessToast("Pass configuration reverted.");
      const historyResult = await apiClient.getDocumentHistory(
        businessId,
        PASS_CONFIGURATION_DOCUMENT_TYPE,
        documentId
      );

      if (historyResult.ok) {
        setHistoryItems(historyResult.data.Results ?? []);
      }
    } catch (error) {
      console.error("Revert pass configuration failed:", error);
      showErrorToast(
        "An error occurred while reverting the pass configuration."
      );
    } finally {
      setRevertingHistoryId(null);
    }
  };

  const handlePreviewPass = (historyId: string | null | undefined) => {
    if (!documentId || !historyId) {
      return;
    }

    navigate(
      `/admin/businesses/${businessId}/pass-configurations/${documentId}?historyId=${encodeURIComponent(
        historyId
      )}`
    );
    onClose();
  };

  const sortedHistoryItems = useMemo(() => {
    return [...historyItems].sort((a, b) => {
      if (a.IsCurrent !== b.IsCurrent) {
        return a.IsCurrent ? -1 : 1;
      }

      const versionDiff = (b.Version ?? 0) - (a.Version ?? 0);
      if (versionDiff !== 0) {
        return versionDiff;
      }

      const aUpdated = a.Meta?.DateUpdated
        ? new Date(a.Meta.DateUpdated).getTime()
        : 0;
      const bUpdated = b.Meta?.DateUpdated
        ? new Date(b.Meta.DateUpdated).getTime()
        : 0;
      return bUpdated - aUpdated;
    });
  }, [historyItems]);

  if (!isOpen || !passConfiguration) return null;

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        placement="right"
        size="xl"
        preserveScrollBarGap
      >
        <DrawerOverlay />
        <DrawerContent maxWidth="900px" width="100%">
          <DrawerCloseButton />
          <DrawerHeader textTransform="uppercase">
            Update History: {passName}
          </DrawerHeader>
          <DrawerBody>
            <Text fontSize="sm" color="gray.600" mb={4}>
              {INFO_TEXT}
            </Text>

            {isLoading && (
              <Flex justify="center" py={10}>
                <Spinner />
              </Flex>
            )}

            {!isLoading && sortedHistoryItems.length === 0 && (
              <Box py={6} textAlign="center">
                <Text color="gray.600">No update history found.</Text>
              </Box>
            )}

            {!isLoading && sortedHistoryItems.length > 0 && (
              <Box w="100%" overflowX="auto">
                <Table size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Version</Th>
                      <Th>Updated By</Th>
                      <Th>Last Updated</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sortedHistoryItems.map((item, index) => {
                      const isCurrent = item.IsCurrent === true;
                      const rowKey =
                        item.HistoryId ||
                        `${item.OriginalId ?? documentId}-current`;

                      return (
                        <Tr
                          key={rowKey}
                          bg={index % 2 === 1 ? "gray.50" : undefined}
                        >
                          <Td whiteSpace="nowrap">
                            {formatHistoryVersion(item.Version)}
                          </Td>
                          <Td whiteSpace="nowrap">
                            {item.UpdatedBy?.UserName ?? "—"}
                          </Td>
                          <Td whiteSpace="nowrap">
                            <HStack spacing={2}>
                              <Text as="span">
                                {formatLastUpdated(item.Meta?.DateUpdated)}
                              </Text>
                              {isCurrent && (
                                <Badge
                                  colorScheme="green"
                                  borderRadius="full"
                                  px={2}
                                  textTransform="uppercase"
                                >
                                  Current
                                </Badge>
                              )}
                            </HStack>
                          </Td>
                          <Td whiteSpace="nowrap">
                            {!isCurrent && (
                              <HStack spacing={4}>
                                <Button
                                  variant="link"
                                  colorScheme="cherryButton"
                                  size="sm"
                                  fontWeight="normal"
                                  onClick={() => handleRevert(item)}
                                  isDisabled={
                                    !item.HistoryId || !!revertingHistoryId
                                  }
                                  isLoading={
                                    revertingHistoryId === item.HistoryId
                                  }
                                >
                                  Revert
                                </Button>
                                <Button
                                  variant="link"
                                  colorScheme="cherryButton"
                                  size="sm"
                                  fontWeight="normal"
                                  onClick={() =>
                                    handlePreviewPass(item.HistoryId)
                                  }
                                  isDisabled={!item.HistoryId}
                                >
                                  Preview Pass
                                </Button>
                              </HStack>
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      {confirmationElement}
    </>
  );
};
